import type { VisualSignalEvent, VisualSignalFrame } from '../signals/VisualSignal.js';
import type { ArtStateValues } from './ArtState.js';
import type { QualityContext } from '../render/RenderSettings.js';
import type { VisualMutation } from './VisualMutation.js';
import type { VisualRandom } from './VisualRandom.js';
import type { WaveEvent } from '../wave/WaveEvent.js';
import type { VisualFieldReader } from '../field/SharedVisualField.js';

/**
 * What a visual renderer is, from the engine's point of view.
 *
 * Note what is *not* here: no scene, no camera, no canvas, no three. A renderer
 * that draws with three is handed its scene when it is constructed, by a factory
 * that lives in the view layer; the engine only ever calls these methods. That
 * is what lets this file — and the engine that drives it — stay in the
 * platform-free project, and it is what will let a renderer that draws to a 2D
 * canvas, or to nothing at all, satisfy the same interface.
 *
 * A renderer owns its own state entirely. The engine does not reach inside one,
 * and two layers of the same renderer type are two instances with two seeds, not
 * one instance drawing twice.
 */

/** What the renderer is being asked to draw, this frame. */
export interface VisualFrameContext {
  /** Song time, from `MusicClock`. The only clock the visual side may use. */
  readonly time: number;
  readonly deltaTime: number;
  /** Every signal's level this frame. */
  readonly signals: VisualSignalFrame;
  /** This layer's resolved settings, after mapping has been applied. */
  readonly layer: ResolvedLayerState;
  /** This layer's own reproducible stream. */
  readonly random: VisualRandom;
  /**
   * What the run has done to the piece so far.
   *
   * Shared by every layer, unlike `layer`, because it describes the artwork
   * rather than one renderer's configuration. A renderer may read it or ignore
   * it entirely.
   */
  readonly art: ArtStateValues;
  /**
   * What the other renderers have said about the picture.
   *
   * Always present, and empty when nothing has published — a renderer that
   * reads it gets zeroes in a recipe that did not include whatever it was
   * hoping to find, which is the correct behaviour rather than a special
   * case to handle. See `SharedVisualField`.
   */
  readonly field: VisualFieldReader;
}

/** A layer's live values: its recipe settings plus whatever mapping computed. */
export interface ResolvedLayerState {
  readonly id: string;
  readonly opacity: number;
  readonly intensity: number;
  readonly zDepth: number;
  readonly blendMode: BlendMode;
  readonly audioReactivity: number;
  /** Static parameters from the recipe, overlaid with mapped signal values. */
  readonly parameters: Readonly<Record<string, number | string | boolean>>;
}

export const BLEND_MODES = ['normal', 'add', 'multiply', 'screen'] as const;
export type BlendMode = (typeof BLEND_MODES)[number];

export function isBlendMode(value: string): value is BlendMode {
  return (BLEND_MODES as readonly string[]).includes(value);
}

export interface VisualRenderer {
  /** The renderer type this instance is, e.g. `MANDALA`. */
  readonly id: string;

  /** Called once, before any update. Build geometry and materials here. */
  initialize(context: VisualFrameContext): void;

  /** Back to the state a fresh run expects, without rebuilding resources. */
  reset(): void;

  /** Advance internal state. Called every frame, before `render`. */
  update(context: VisualFrameContext): void;

  /**
   * A moment happened. Optional: a renderer that only reads levels omits it.
   *
   * Called between frames, so an implementation should record what it needs and
   * act on it in `update` rather than doing work here.
   */
  handleSignal?(event: VisualSignalEvent, context: VisualFrameContext): void;

  /**
   * Change something, as instructed by the recipe.
   *
   * Optional, and a renderer only answers the actions it has a meaning for.
   * Ignoring an action is correct behaviour, not a gap: `SPLASH` means nothing
   * to a text panel and the recipe should still be allowed to ask for it. The
   * engine folds every dispatched mutation into `ArtState` regardless, so a
   * mutation nobody implements still changes the state of the piece.
   */
  applyMutation?(item: VisualMutation, context: VisualFrameContext): void;

  /**
   * A disturbance happened, here, and this material was chosen for it.
   *
   * Optional, like the rest. A renderer that implements this is agreeing to
   * one thing: the event says *where*, *how hard* and *which way*, and the
   * renderer decides entirely what that looks like. The same event handed to
   * two materials has to come back as two different pictures, or the wave
   * layer has bought nothing over calling one renderer directly.
   *
   * Position is in field units — 1 is the playfield radius — so a renderer
   * multiplies by its own radius. That is what lets a spawn log be compared
   * between a live run and a 4K offline re-render.
   */
  spawnWave?(event: WaveEvent, context: VisualFrameContext): void;

  /**
   * What this renderer can tell the others about the picture it is making.
   *
   * Optional, and most renderers do not have one. The engine publishes it on
   * this layer's behalf, so a renderer never learns who is reading — which
   * is the whole point of routing it through the engine rather than letting
   * two renderers hold references to each other.
   */
  readonly fieldReader?: VisualFieldReader;

  /**
   * Draw. Separate from `update` so that a future compositor can update every
   * layer, then draw them in a different order, or to different targets.
   */
  render(context: VisualFrameContext): void;

  /**
   * The frame has been composited and is on screen. Optional.
   *
   * For work that writes to a texture the frame itself samples. Doing that
   * during `update` puts a write before the read *within one frame*, and a
   * texture written and then sampled in the same frame does not come back
   * reliably — the read is undefined, and a full-screen quad reading it can
   * take the whole frame with it. Here the order is read-then-write: this
   * frame shows what the last one produced, and the write lands after
   * everything that could have sampled it.
   */
  postRender?(context: VisualFrameContext): void;

  /** Recipe parameters changed. Renderers validate their own. */
  setParameters(parameters: Readonly<Record<string, number | string | boolean>>): void;

  /**
   * How much work this renderer should do.
   *
   * Optional, and separate from parameters on purpose: a recipe describes what
   * a layer *is*, and quality describes how finely it is drawn. The same recipe
   * has to be renderable at LIVE for play and at ULTRA for an offline export,
   * so the two cannot be the same setting.
   *
   * Called before `initialize`, so a renderer may size its pools from it.
   */
  setQuality?(quality: QualityContext): void;

  /** Whether this renderer currently contributes anything to the frame. */
  setEnabled(enabled: boolean): void;

  dispose(): void;
}

/**
 * Builds a renderer of a given type.
 *
 * Supplied by whoever owns the drawing surface. The engine holds a map of these
 * and nothing else, which is the entire extent of its knowledge of three.
 */
export type VisualRendererFactory = (layerId: string, seed: number) => VisualRenderer;
